import { Dropdown, Text } from '@loomhq/lens';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import styles from './styles.module.less';

function MenuButton({
  name,
  items,
  children,
  collapsed,
}: {
  name: string;
  items: Record<string, undefined>[];
  children: JSX.Element;
  collapsed: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = `${name}-panel`;

  useEffect(() => setOpen(false), [collapsed]);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const buttonTrigger = (
    <button
      className={cn(styles.menuItem)}
      aria-expanded={collapsed ? undefined : open}
      aria-haspopup={collapsed ? 'true' : undefined}
      aria-controls={panelId}
      onClick={() => setOpen(!open)}
      ref={triggerRef}
    >
      {children}
    </button>
  );

  // Since the dropdown isn't opened with a trigger that detects clicks outside of it automatically
  // and close the dropdown, we need to add a click handler to handle this case explicitly
  useEffect(() => {
    if (!collapsed) {
      return;
    }

    function handleClickOutside(event) {
      if (
        open &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [open, collapsed]);

  // For the collapsed state, the dropdown is rendered without a trigger and managed
  // by the button trigger. This is to keep the settings sidebar item visually consistent with other
  // menu links when it is collapsed or expanded
  return (
    <div>
      {buttonTrigger}
      {collapsed && (
        <Dropdown
          onOuterClick={() => setOpen(false)}
          options={items}
          isOpen={open}
        />
      )}
      {!collapsed && (
        <div className={styles.menuPanel} id={panelId} hidden={!open}>
          {items.map(item => (
            <div key={item.path}>
              <a
                className={styles.menuItem}
                href={item.path}
                aria-current={
                  window.location.pathname.startsWith(String(item.path))
                    ? 'page'
                    : undefined
                }
              >
                <Text fontWeight="medium">{item.title}</Text>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MenuButton;
